import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Image,
  Input,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
  useDisclosure,
} from "@chakra-ui/react";
import {
  addAdvertisementImage,
  addVerificationImage,
  useDeleteAdImage,
  useDeleteVerificationImage,
  useProfileUpdate,
} from "api/auth";
import DeleteIcon from "assets/svg/DeleteIcon";
import Alert from "generics/Alert";
import ImageInput from "generics/ImageInput";
import Map from "generics/Map";
import PageTitle from "generics/PageTitle";
import ToolbarPortal from "generics/ToolbarPortal";
import { checkImage } from "helpers";
import { useAuth } from "providers/AuthProvider";
import { useImageModal } from "providers/ImageModalProvider";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { AiFillDelete, AiOutlinePlus } from "react-icons/ai";
import { useMutation } from "react-query";

export default function ProfilePage() {
  const auth = useAuth();
  const { register, handleSubmit, errors, trigger, control } = useForm({
    defaultValues: auth.lab,
  });
  const [phone, setPhone] = useState<any>();
  const [phoneNumbers, setPhoneNumbers] = useState<any>([]);
  const [updateProfile, updateProfileInfo] = useProfileUpdate();
  const phoneModal = useDisclosure();
  const deleteModal = useDisclosure();

  useEffect(() => {
    if (auth.lab) {
      const phone = auth.lab?.phoneNumbers ?? auth.lab?.phoneNumber;
      const number = typeof phone === "string" ? [phone] : phone;
      setPhoneNumbers(number ?? []);
    }
  }, [auth?.lab]);

  function onSubmit(values: any) {
    if (!values.createdAt) {
      values.createdAt = Date.now();
    }
    values.updatedAt = Date.now();
    updateProfile({ ...values, phoneNumbers });
  }

  function addPhoneNumber() {
    phoneNumbers.push(phone);
    setPhoneNumbers([...phoneNumbers]);
    setPhone("");
    phoneModal.onClose();
  }

  return (
    <>
      <Modal
        isOpen={phoneModal.isOpen}
        onClose={() => {
          phoneModal.onClose();
        }}
      >
        <ModalOverlay></ModalOverlay>
        <ModalContent bg="white" borderRadius="4px" maxW="50vw" maxH="50vh">
          <ModalHeader bg="modalHeader.500" color="white">
            Add Phone Number
          </ModalHeader>
          <form onSubmit={handleSubmit(addPhoneNumber)}>
            <Stack direction="row" margin="15px">
              <FormControl id="phoneNumbers" isRequired>
                <FormLabel>Phone Number</FormLabel>
                <Input
                  type="number"
                  name="phoneNumbers"
                  placeholder="Phone Number"
                  maxLength={16}
                  minLength={7}
                  errors={errors}
                  onChange={(e) => {
                    setPhone(e.target.value);
                  }}
                  required
                />
              </FormControl>
            </Stack>
            <ModalFooter justifyContent="flex-start">
              <Button type="submit" mr={3}>
                Add
              </Button>
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={phoneModal.onClose}
              >
                Cancel
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
      <Box px={12} py={6}>
        <ToolbarPortal>
          <Flex justify="space-between" flexGrow={1} align="center">
            <PageTitle flexGrow={1}>Profile</PageTitle>
          </Flex>
        </ToolbarPortal>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={6}>
            <ImageInput
              src={auth?.lab?.photoUrl}
              alt={auth?.lab?.name || "Lab Image"}
              errors={errors}
              register={register}
              trigger={trigger}
            />
            <Stack direction="row">
              <FormControl id="name" isRequired>
                <FormLabel>Name</FormLabel>
                <Input
                  type="text"
                  name="name"
                  placeholder="Name"
                  ref={register}
                />
              </FormControl>
            </Stack>
            <Stack direction="row">
              <FormControl id="address" isRequired>
                <FormLabel>Address</FormLabel>
                <Input
                  type="text"
                  name="address"
                  placeholder="Address"
                  ref={register}
                />
              </FormControl>
            </Stack>
            <PhoneNumber
              deleteModal={deleteModal}
              phoneNumbers={phoneNumbers}
              setPhoneNumbers={setPhoneNumbers}
              phoneModal={phoneModal}
            />
            <FormControl id="description" isRequired>
              <FormLabel>Description</FormLabel>
              <Textarea
                type="text"
                name="description"
                placeholder="Description"
                ref={register}
              />
            </FormControl>

            <FormControl id="location">
              <FormLabel>Map</FormLabel>
              <Controller
                control={control}
                defaultValue={auth?.lab?.location}
                name="location"
                render={({ onChange, value }) => (
                  <Map value={value} onChange={onChange} />
                )}
              />
            </FormControl>
            <Stack direction="row">
              <FormControl id="panNumber" isRequired>
                <FormLabel>PAN Number</FormLabel>
                <Input
                  type="number"
                  name="panNumber"
                  placeholder="PAN Number"
                  ref={register}
                />
              </FormControl>
              <FormControl id="website">
                <FormLabel>Website</FormLabel>
                <Input
                  type="text"
                  name="website"
                  placeholder="Website"
                  ref={register}
                />
              </FormControl>
            </Stack>
            <Stack direction="row">
              <FormControl id="bankAccountNumber" isRequired>
                <FormLabel>Bank Account Number</FormLabel>
                <Input
                  type="text"
                  name="bankAccountNumber"
                  placeholder="Bank Account Number"
                  ref={register}
                />
              </FormControl>
              <FormControl id="bankAccountName" isRequired>
                <FormLabel>Bank Account Name</FormLabel>
                <Input
                  type="text"
                  name="bankAccountName"
                  placeholder="Bank Account Name"
                  ref={register}
                />
              </FormControl>
            </Stack>
            <Stack direction="row">
              <FormControl id="bankName" isRequired>
                <FormLabel>Bank Name</FormLabel>
                <Input
                  type="text"
                  name="bankName"
                  placeholder="Bank Name"
                  ref={register}
                />
              </FormControl>
              <FormControl id="bankBranch" isRequired>
                <FormLabel>Bank Branch</FormLabel>
                <Input
                  type="text"
                  name="bankBranch"
                  placeholder="Bank Branch"
                  ref={register}
                />
              </FormControl>
            </Stack>
            <Stack direction="row">
              <FormControl id="opensAt" isRequired>
                <FormLabel>Opening Time</FormLabel>
                <Input type="time" name="opensAt" ref={register} />
              </FormControl>
              <FormControl id="closesAt" isRequired>
                <FormLabel>Closing Time</FormLabel>
                <Input type="time" name="closesAt" ref={register} />
              </FormControl>
            </Stack>
            <Button type="submit" isLoading={updateProfileInfo.isLoading}>
              Update
            </Button>
          </Stack>
        </form>
      </Box>
      <Divider />
      <VerificationImages />
      <Divider />
      <LabAdvertisementImages />
    </>
  );
}

function PhoneNumber({
  phoneNumbers,
  setPhoneNumbers,
  phoneModal,
  deleteModal,
}: any) {
  function deletePhone(index: number) {
    const nos: Array<string> = phoneNumbers.filter(
      (phone: string, i: number) => i !== index
    );
    setPhoneNumbers([...nos]);
  }

  return (
    <Stack direction="column">
      <Button
        onClick={() => phoneModal.onOpen()}
        variant="link"
        justifyContent="flex-start"
      >
        Add Phones
      </Button>
      {phoneNumbers && phoneNumbers?.length ? (
        <div>
          <FormLabel>Phone Numbers</FormLabel>
          <ul style={{ marginLeft: 21 }}>
            {phoneNumbers?.map((nos: string, i: number) => {
              <Alert
                confirmText="Delete"
                isOpen={deleteModal.isOpen}
                onOk={() => deletePhone(i)}
                title="Delete Phone Number"
                message={"Are you sure you want to delete this phone number?"}
                onClose={deleteModal.onClose}
                isLoading={false}
              />;
              return (
                <Stack direction="row" alignItems="center">
                  <li>{nos} </li>
                  <HStack>
                    <IconButton
                      bg="transparent"
                      color="red.300"
                      icon={<DeleteIcon />}
                      aria-label="delete-test"
                      size="sm"
                      onClick={() => {
                        deletePhone(i);
                      }}
                    ></IconButton>
                  </HStack>
                </Stack>
              );
            })}
          </ul>
        </div>
      ) : null}
    </Stack>
  );
}

interface IFilePreview {
  file: any;
  deleteMutation: any;
  disabled?: boolean;
}

function FilePreview({ file, deleteMutation, disabled }: IFilePreview) {
  const [deleteImage, deleteImageInfo] = deleteMutation();
  const imageModalContext = useImageModal();
  return (
    <Box position="relative">
      <IconButton
        position="absolute"
        top="2"
        right="2"
        p={0}
        size="xs"
        onClick={() => deleteImage(file)}
        aria-label="delete photo"
        icon={<AiFillDelete />}
        isLoading={deleteImageInfo.isLoading}
        disabled={disabled}
      />

      <Image
        boxSize="100px"
        borderRadius="md"
        bg="gray.700"
        boxShadow="base"
        objectFit="cover"
        cursor={checkImage(file.name) ? "pointer" : "inherit"}
        src={file.url}
        onClick={() => imageModalContext.open(file)}
        fallbackSrc={`https://via.placeholder.com/150/?text=${file.name
          .split(".")[1]
          .toUpperCase()}`}
      />
    </Box>
  );
}

function VerificationImages() {
  const auth = useAuth();

  const newImageRef = useRef<any>();

  const [addImage, addImageInfo] = useMutation(addVerificationImage);

  function onImageSelect(e: React.ChangeEvent<HTMLInputElement>) {
    addImage(e.target.files?.[0]);
  }

  return (
    <Box px="12" py="6">
      <Text fontSize="2xl" fontWeight="500" color="black.500">
        Verification Images
      </Text>
      <Stack direction="row" pt="2">
        {auth.lab?.verificationDocuments?.map((document: any) => {
          if (typeof document === "string") return null;

          return (
            <FilePreview
              key={document.name}
              file={document}
              deleteMutation={useDeleteVerificationImage}
              disabled={auth.lab?.verified}
            />
          );
        })}
        <Flex boxSize="100px" justifyContent="center" alignItems="center">
          <input
            type="file"
            name="image"
            id="image"
            hidden
            ref={newImageRef}
            accept="image/*"
            onChange={onImageSelect}
          />
          <IconButton
            aria-label="Add Image"
            size="lg"
            h="100px"
            w="100px"
            variant="outline"
            isLoading={addImageInfo.isLoading}
            disabled={addImageInfo.isLoading}
            hidden={!auth.lab?.verified}
            onClick={() => newImageRef.current!.click()}
            icon={<AiOutlinePlus size="80px" />}
          />
        </Flex>
      </Stack>
    </Box>
  );
}

function LabAdvertisementImages() {
  const auth = useAuth();
  const newImageRef = useRef<any>();

  const [addImage, addImageInfo] = useMutation(addAdvertisementImage);

  function onImageSelect(e: React.ChangeEvent<HTMLInputElement>) {
    addImage(e.target.files?.[0]);
  }

  return (
    <Box px="12" py="6">
      <Text fontSize="2xl" fontWeight="500" color="black.500">
        Advertisement Images
      </Text>
      <Stack direction="row" pt="2">
        {auth.lab?.images?.map((document: any) => (
          <FilePreview
            key={document.name}
            file={document}
            deleteMutation={useDeleteAdImage}
          />
        ))}
        <Flex boxSize="100px" justifyContent="center" alignItems="center">
          <input
            type="file"
            name="image"
            id="image"
            hidden
            ref={newImageRef}
            accept="image/*"
            onChange={onImageSelect}
          />
          <IconButton
            aria-label="Add Image"
            size="lg"
            h="100px"
            w="100px"
            variant="outline"
            isLoading={addImageInfo.isLoading}
            disabled={addImageInfo.isLoading}
            hidden={!auth.lab?.verified}
            onClick={() => newImageRef.current!.click()}
            icon={<AiOutlinePlus size="80px" />}
          />
        </Flex>
      </Stack>
    </Box>
  );
}
